import { useCallback, useMemo, type ReactElement } from 'react'
import { useQueryState } from 'nuqs'
import { parseAsZodSchema } from '@util'
import { isDefined, isNotEmpty, QuizQuestionIdSchema, type QuizQuestionId } from '@terros/common'
import { useQueryDisclosure } from '@hooks'
import { useTranslation } from '@front/model'
import { Page, TButton, TDrawer } from '@components'
import { QuizPageContainer } from '../quiz_.$quizId/QuizPageContainer'
import { useQuizContext } from '../quiz_.$quizId/context'
import { QuestionList } from './QuestionList'
import { EditQuestion } from './EditQuestion'
import { AddQuestion } from './AddQuestion'
import { createFileRoute } from '@tanstack/react-router'

function QuizQuestionsRoute(): ReactElement {
  const { quiz, questions, refetch, isFetching } = useQuizContext()
  const [isAddDrawerOpen, { open: openAddDrawer, close: closeAddDrawer }] = useQueryDisclosure('add')
  const [selectedQuestionId, setSelectedQuestionId] = useQueryState<QuizQuestionId>(
    'questionId',
    parseAsZodSchema(QuizQuestionIdSchema)
  )

  const { t } = useTranslation()

  const selectedQuestion = useMemo(
    () => questions.find((q) => q.questionId === selectedQuestionId),
    [questions, selectedQuestionId]
  )

  const handleClose = useCallback(() => setSelectedQuestionId(null), [setSelectedQuestionId])

  const hasQuestions = isNotEmpty(questions)

  return (
    <QuizPageContainer>
      <Page.Heading isFetching={isFetching} refetch={refetch} title='Questions'>
        {hasQuestions && (
          <TButton icon='add' onClick={openAddDrawer}>
            Add Question
          </TButton>
        )}
      </Page.Heading>
      <Page.Content scrollArea>
        {hasQuestions ? (
          <QuestionList questions={questions} quizId={quiz.quizId} onSelect={setSelectedQuestionId} />
        ) : (
          <Page.Hero icon='question_mark' title='No Questions Yet'>
            <TButton icon='add' onClick={openAddDrawer}>
              {t('Add Question')}
            </TButton>
          </Page.Hero>
        )}
      </Page.Content>

      <TDrawer size='lg' opened={isDefined(selectedQuestion)} title='Edit Question' onClose={handleClose}>
        {selectedQuestion && <EditQuestion question={selectedQuestion} onClose={handleClose} />}
      </TDrawer>
      <TDrawer size='lg' opened={isAddDrawerOpen} onClose={closeAddDrawer} title='Add Question'>
        <AddQuestion close={closeAddDrawer} />
      </TDrawer>
    </QuizPageContainer>
  )
}

export const Route = createFileRoute('/quiz_/$quizId/questions/')({
  component: QuizQuestionsRoute,
})
